<template>
    <table
        class="identify-properties-viewer"
        :style="componentStyle">

        <thead
            v-if="title"
            :key="title"
            :style="titleStyle"
            class="identify-properties-viewer-title">
            <tr>
                <th colSpan="2">{{title}}</th>
            </tr>
        </thead>

        <tbody
            class="identify-properties-viewer-body">
            <tr
                v-for="item in bodyItems"
                :key="`prop-${item.key}`"
                :style="listStyle">
                <td class="">{{ item.label }}</td>
                <td>
                    <template v-if="item.actions">
                        <template v-for="action in item.actions">
                            <template v-if="action.type == 'popup'">
                                <a :key="`${action.keyBind}`"
                                    :href="action.href"
                                    @click.prevent="$emit('emitOpenPopup', $event.target.href, feature, action.params, action.tools);return false;">
                                    {{ action.text }}
                                </a>
                            </template>
                            <template v-else-if="action.type == 'link'">
                                <a :key="`${action.keyBind}`"
                                    :href="action.href"
                                    :target="action.target">
                                    {{ action.text }}
                                </a>
                            </template>
                            <template v-else-if="action.type == 'event'">
                                <a :key="`${action.keyBind}`"
                                    href="#"
                                    @click.prevent="$emit('dispatchEvent', action.params, feature);return false;">
                                    {{ action.text }}
                                </a>
                            </template>
                            <template v-else-if="action.type == 'image'">
                                <img :key="`${action.keyBind}`"
                                    :src="action.href"
                                    :alt="`image`"
                                    :class="action.params?.class ?? ''">
                            </template>
                        </template>
                    </template>
                    <template v-else>
                        <ul class="list-group list-group-flush">
                            <template v-for="(res, resIdx) in item.val">
                                <li class="list-group-item"
                                    :key="`${item.keyBind}-${resIdx}`">
                                    <template v-if="item.type == 'image'">
                                        <a
                                            :href="res.val"
                                            :target="item?.params?.target ?? '_blank'">
                                            <img
                                                :src="res.val"
                                                :alt="`image`"
                                                :class="['img-fluid', item?.params?.class ?? '']">
                                        </a>
                                    </template>
                                    <template v-else-if="item.type == 'link'">
                                        <a
                                            :href="res.val"
                                            :target="item?.params?.target ?? '_blank'"
                                            :class="item?.params?.class ?? ''">
                                            {{ res.text }}
                                        </a>
                                    </template>
                                    <template v-else>
                                        <div v-html="res.text">
                                        </div>
                                    </template>
                                </li>
                            </template>
                        </ul>
                    </template>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script>
    import {formatString, formatUrl, isString, isJson, slugify} from '@config/utils';

    export default {
        name: 'RowPropertiesViewer',
        mixins: [
        ],
        components: {

        },
        props: {
            title: {
                type: String,
                default: '',
            },
            titleStyle: {
                type: Object,
                default: ()=>{},
            },
            listStyle: {
                type: Object,
                default: ()=>{},
            },
            componentStyle: {
                type: Object,
                default: ()=>{},
            },
            feature: {
                type: Object,
                default: ()=>{},
            },
            featureCfg: {
                type: Object,
                default: ()=>{},
            },
            featureInfo: {
                type: Object,
                default: ()=>{},
            },
        },
        computed: {
            featureProperties() {
                return this.feature?.properties ?? {};
            },
            featureCfgProperties() {
                return this.featureCfg?.properties ?? [];
            },
            visibleFeaturePropertiesKeys() {
                return this.featureCfgProperties.map(item => item.source_name) ?? [];
            },
            bodyItems() {
                return Object.keys(this.featureProperties)
                    .filter(key => this.visibleFeaturePropertiesKeys.length == 0 ||
                        this.visibleFeaturePropertiesKeys.length && this.visibleFeaturePropertiesKeys.includes(key))
                    .map((key, index) => {
                        const cfgProperty = this.featureCfgProperties.find(item => item.source_name == key);
                        const _type = (cfgProperty?.type ?? 'string').toLowerCase();
                        const _params = cfgProperty?.params ?? {separator: ','};
                        const _label = formatString(cfgProperty?.name ?? key, this.featureProperties, '{', '}');
                        let _text = this.featureProperties[key];
                        _text = isString(_text) ? _text : JSON.stringify(_text);
                        let _value = [];
                        switch (_type) {
                            case 'image':
                            case 'link':
                                let _vals = [];
                                if(_params.separator) {
                                    _vals = _text.split(_params.separator).map(item => item.trim()).filter(x => x);
                                }
                                _value = _vals.map((val, idx) => {
                                    let href = _params.href ?? val;
                                    href = href ? formatUrl(href, this.featureProperties, '{', '}') : null;
                                    if(href) {
                                        let scheme = `${window.location.protocol}//`;
                                        href = href.replace('http://', scheme);
                                        href = href.split(scheme).filter(x => x).slice(-1).pop();
                                        href = href ? `${scheme}${href}` : null;
                                    }
                                    return {
                                        text: this.textVal(val),
                                        val: href,
                                        keyBind: `prop-${index}-${slugify(key)}-${slugify(_type)}-${idx}`,
                                    }
                                });
                                break;
                            default:
                                _value.push({
                                    text: this.textVal(_text),
                                    val: _text,
                                    keyBind: `prop-${index}-${slugify(key)}-${slugify(_type)}`,
                                });
                                break;
                        }

                        let _actions = this.actionVal(index, key, _text, this.featureProperties);
                        return {
                            key: key,
                            label: _label,
                            val: _value,
                            type: _type,
                            actions: _actions,
                            params: _params,
                            order: cfgProperty?.order ?? 1,
                        }
                    }).sort((a,b) => a.order - b.order);
            },
        },
        methods: {
            textVal(val){
                return val ? val.trim().replace('null', '').replace('""', '') : '';
            },
            actionVal(index, key, val, properties){
                let _properties = JSON.parse(JSON.stringify(properties));
                let actions = this.featureInfo.actions || [];
                let fAction = actions.find(action => action.prop===key);
                if(fAction){
                    let action = JSON.parse(JSON.stringify(fAction));
                    let acl = action.acl_code;
                    if(!acl || acl && this.$auth.check(acl)){
                        let _type = action.action;
                        let actionParams = action.params ?? {};

                        // tools
                        let tools = [];
                        let actionTools = actionParams?.tools ?? {};
                        Object.keys(actionTools).forEach(key => {
                            let tool = actionTools[key];
                            let acl = tool.acl_code;
                            if(!acl || acl && this.$auth.check(acl)){
                                this.iterate(tool, '', _properties);
                                tools[key] = tool;
                            }
                        });

                        let result = [];
                        let items = [val]
                        if(actionParams?.separator) {
                            items = val.split(actionParams?.separator).map(item => item.trim());
                        }
                        items.forEach((item, idx) => {
                            let _val = item;
                            _properties[key] = _val;

                            let _params = actionParams?.params ?? actionParams;
                            _params = JSON.stringify(_params);
                            _params = formatString(_params, _properties, '{', '}');
                            // for (let v in _properties) {
                            //     var regexp = new RegExp(':'+v+'', 'gi');
                            //     _params = _params.replace(regexp, _properties[v]);
                            // }
                            if(actionParams?.params) {
                                actionParams.params = JSON.parse(_params);
                                _params = actionParams;
                            }
                            let href = action.href ? formatUrl(action.href, _properties, '{', '}') : null;
                            if(href) {
                                let scheme = `${window.location.protocol}//`;
                                href = href.replace('http://', scheme);
                                href = href.split(scheme).filter(x => x).slice(-1).pop();
                                href = href ? `${scheme}${href}` : null;
                            }

                            result.push({
                                text: this.textVal(_val),
                                type: _type,
                                href: href,
                                target: actionParams?.target ?? null,
                                params: isString(_params) && isJson(_params) ? JSON.parse(_params) : _params,
                                tools: tools,
                                keyBind: `action-${index}-${idx}-${slugify(_type)}-${slugify(key)}`,
                            });
                        });
                        return result;
                    }
                }
                return null;
            },
            iterate(obj, stack, properties) {
                for (let property in obj) {
                    if (obj.hasOwnProperty(property)) {
                        if (typeof obj[property] == "object") {
                            this.iterate(obj[property], stack + '.' + property, properties);
                        } else {
                            formatString(obj[property], properties, '{', '}');
                        }
                    }
                }
            }
            // renderVal(key, val) {
            //     val = val.trim().replace('null', '').replace('""', '');
            //     let links = this.featureInfo.links || [];
            //     let link = links.find(link => link.prop===key);
            //     if(link){
            //         let acl = link.acl_code;
            //         if(!acl || acl && this.$auth.check(acl)){
            //             let url = link.href.replace(`:${key}`, val);
            //             switch(link.target.toLowerCase()){
            //                 case 'popup':
            //                     return `<a href="${url}" >${val}</a>`;
            //                 default:
            //                     return `<a href="${url}" target="${link.target}">${val}</a>`
            //             }
            //         }
            //         return val;
            //     }
            //     return val;
            // },
        }
    }

</script>

<style scoped lang="scss">
    .identify-properties-viewer {
        width: 100%;
        min-width: 256px;
        background-color: transparent;
        color: #6e6e6e;

        &table {
            border-collapse: collapse;
            border-spacing: 0;
        }

        thead {
            font-size: 1em;
            font-family: var(--city-semibold-font-family);
            background-color: #f2f2f2;
            color: #000000;
            margin-bottom: 4px;

            th {
                padding: 0 4px 4px;
                font-weight: normal;
                word-break: break-word;
            }
        }

        tbody {
            background-color: #ffffff;
            font-size: .9rem;

            tr {
                border-bottom: solid 1px #dddddd;
            }

            td {
                &:first-child {
                    padding: .25rem .5rem .25rem 0;
                    font-family: var(--city-semibold-font-family);
                    vertical-align: top;
                    min-width: 120px;
                }

                &:last-child {
                    text-align: right;
                    word-break: break-word;
                    overflow-wrap: break-word;

                    ul li {
                        padding: .25rem 0 .25rem .5rem;
                    }
                }
            }
        }

        + .identify-properties-viewer {
            margin-top: 1rem;
        }
    }
</style>